import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import OptimizedImage from '../../components/common/OptimizedImage';
import OptimizedLogo from '../../components/common/OptimizedImage/OptimizedLogo';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { Shield, Microscope, Lock, ChartBar, ArrowRight } from 'lucide-react';
// import './MainPage.css'; // Removed

const MainPage: React.FC = () => {
  const { t, currentLanguage } = usePublicTranslation();

  // Helper to safely access nested properties if types are loose
  const hero = t.home?.hero || {};
  const pillars = t.home?.pillars?.items || [];
  const solutions = t.solutions?.items || {};
  const content = t.home || {}; // General home content fallback
  const faqItems = t.faq?.home?.items || [];



  // Replicating the mailto logic for "Fale com um especialista" preserving behavior
  const handleMailto = () => {
    const subject = encodeURIComponent('Consulta sobre Serviços NexuCapital');
    const body = encodeURIComponent(`Olá,\n\nGostaria de obter mais informações sobre os serviços da NexuCapital.\n\nAguardo retorno.`);
    window.location.href = `mailto:contato@nexucapital.com.br?subject=${subject}&body=${body}`;
  }

  // Generate FAQ Schema for SEO
  const generateFAQSchema = (items: any[]) => {
    if (!items || items.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": items.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
  };

  useSEO({
    title: content.seo?.title || "NexuCapital - Intermediação em Ativos Minerários",
    description: content.seo?.description || "Intermediação confiável em ativos minerários com due diligence completo.",
    path: "/",
    language: currentLanguage as 'pt-br' | 'en' | 'es',
    structuredData: [
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' }
      ]),
      generateWebPageSchema({
        name: content.seo?.title || "NexuCapital - Intermediação em Ativos Minerários",
        description: content.seo?.description || "Intermediação confiável em ativos minerários com due diligence completo.",
        url: "/",
        datePublished: "2025-01-20",
        dateModified: "2025-01-20"
      }),
      generateFAQSchema(faqItems)
    ].filter(Boolean)
  });

  return (
    <PublicLayout>

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:h-screen flex items-center justify-center overflow-hidden py-24 md:py-0">
        <div className="absolute inset-0 z-0">
          {/* Detailed Grid Pattern Overlay */}
          <div
            className="absolute inset-0 z-10 opacity-30 pointer-events-none"
            style={{
              backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)" /></svg>')`
            }}
          />

          {/* Main Gradient Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(28,30,34,0.85)_0%,rgba(13,92,85,0.5)_100%)] z-10" />

          <OptimizedImage
            src="/assets/imagens/pexels-urtimud-89-76108288-14263363.jpg"
            alt="NexuCapital Hero"
            className="w-full h-full object-cover"
            fallbackSrc="/assets/imagens/pexels-pixabay-46801.jpg"
            priority={true}
            fetchpriority="high"
          />
        </div>

        <div className="container mx-auto px-4 relative z-20 pt-10 md:pt-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Mobile Logo Only */}
            <div className="flex md:hidden justify-center mb-4 animate-fade-in opacity-90 transition-all">
              <OptimizedLogo
                name="logo-nexucapital-principal-branca"
                alt="NexuCapital"
                className="w-48 h-auto drop-shadow-2xl"
                loading="eager"
              />
            </div>

            <div className="text-white space-y-6 animate-fade-in-up text-center md:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight bg-[linear-gradient(135deg,#ffffff_0%,#c9a227_100%)] bg-clip-text text-transparent pb-2">
                {hero.title}
              </h1>
              <div className="space-y-4 max-w-xl text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed font-light mx-auto md:mx-0">
                <p>
                  {hero.subtitle}
                </p>
                <p>
                  {hero.description}
                </p>
              </div>
              <div className="flex justify-center md:justify-start space-x-4 pt-4">
                <button
                  onClick={handleMailto}
                  className="bg-secondary hover:bg-secondary-dark text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-transform transform hover:scale-105 shadow-lg flex items-center"
                >
                  {hero.primaryCta || "Fale com um especialista"}
                  <ArrowRight size={18} className="ml-2" />
                </button>
              </div>
            </div>

            <div className="hidden md:flex justify-center justify-self-end animate-fade-in opacity-80">
              {/* Visual Element - Logo or Abstract */}
              <OptimizedLogo
                name="logo-nexucapital-principal-branca"
                alt="NexuCapital"
                className="w-80 h-auto opacity-90 drop-shadow-2xl animate-float"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Pillars */}
      <section id="compliance" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-dark mb-4">{t.home?.pillars?.title || "Nossos Pilares"}</h2>
            <div className="w-[60px] h-[3px] bg-gradient-to-r from-primary to-secondary mx-auto rounded-sm"></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {pillars.map((pillar: any, index: number) => {
              const Icon = [Shield, Microscope, Lock, ChartBar][index % 4];
              return (
                <div key={pillar.id} className="bg-white p-6 sm:p-8 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group border-b-4 border-transparent hover:border-secondary text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary/5 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-primary group-hover:text-white transition-all duration-300 text-primary group-hover:scale-110">
                    <Icon className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-dark mb-3">{pillar.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Preview */}
      <section id="solutions" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-dark mb-2">{t.solutions?.title || "Soluções"}</h2>
            <div className="w-[60px] h-[3px] bg-gradient-to-r from-primary to-secondary rounded-sm mt-4 mx-auto"></div>
            <p className="text-gray-600 max-w-lg mt-4 mx-auto">{t.solutions?.description}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {(['operations', 'insights', 'advisory'] as const).map((key) => {
              const item = (solutions as any)[key];
              if (!item) return null;
              return (
                <div
                  key={key}
                  className="relative bg-gradient-to-br from-primary/90 to-primary-dark/85 p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 group text-center h-full flex flex-col items-center overflow-hidden"
                >
                  {/* Gold accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-secondary" />

                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 lg:min-h-[3.5rem] flex items-center">{item.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{item.description}</p>

                  {/* Bottom hover accent */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-secondary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </div>
              )
            })}
          </div>
        </div>
      </section>

    </PublicLayout>
  );
};

export default MainPage;